import {
  PreferredCreatorAgeDetail,
  PreferredCreatorCity,
  PreferredCreatorCountry,
  PreferredCreatorGender,
  PreferredCreatorState,
} from '@/types/campaign';
import {BaseFilterOption, Range} from '@/types/models/search-creators/filter';
import {
  FilterIds,
  FilterSelection,
  FiltersGroupId,
  PlatformFilterIds,
} from '@/types/models/search-creators/filterId';
import locationFetchers, {FetchedLocation} from '@/api/fetchers/location';

export type RangeFilterOptionsReturn = {
  range: Range;
  filterSelectionType: FilterSelection;
  filterGroupId: FiltersGroupId;
  filterId: FilterIds;
  platformFilterId?: PlatformFilterIds;
};

export type SingleSelectionFilterOptionsReturn = {
  option: BaseFilterOption;
  filterSelectionType: FilterSelection;
  filterGroupId: FiltersGroupId;
  filterId: FilterIds;
  platformFilterId?: PlatformFilterIds;
};

export type CampaignFiltersStackResult =
  | RangeFilterOptionsReturn
  | SingleSelectionFilterOptionsReturn
  | null;

export function campaignCreatorPlatformMetricsToSearchOptions(
  unlocksAt: number | undefined,
  locksAbove: number | undefined,
  platformFilterId: PlatformFilterIds
): CampaignFiltersStackResult {
  if (!unlocksAt && !locksAbove) return null;
  return {
    range: {
      min: unlocksAt,
      max: locksAbove,
    },
    filterSelectionType: 'rangeSelection',
    filterGroupId: 'platforms',
    filterId: 'platformMetrics',
    platformFilterId,
  };
}

export default function campaignPreferredAgeToSearchOptions(
  campaignPreferredAge: PreferredCreatorAgeDetail
): CampaignFiltersStackResult {
  if (!campaignPreferredAge.minAge && !campaignPreferredAge.maxAge) return null;
  return {
    range: {min: campaignPreferredAge.minAge, max: campaignPreferredAge.maxAge},
    filterSelectionType: 'rangeSelection',
    filterGroupId: 'creatorDetails',
    filterId: 'age',
  };
}

function getPlatformId(platformId: string): string {
  if (platformId.includes('instagram')) {
    return 'instagram';
  }
  if (platformId.includes('tiktok')) {
    return 'tiktok';
  }
  return platformId;
}

export function campaignPreferredPlatformToSearchOptions(
  platformId: string
): SingleSelectionFilterOptionsReturn | null {
  if (!platformId) return null;
  const selectedPlatformId = getPlatformId(platformId);
  const platformObject = {
    id: selectedPlatformId,
    localeLabelKey: '',
    value: selectedPlatformId,
  };
  return {
    option: platformObject,
    filterSelectionType: 'singleSelection',
    filterGroupId: 'platforms',
    filterId: 'platform',
  };
}

/* TODO: For now the gender filter supports single selection only and set to
    'all' in case that the campaign have multiple genders set */
export function campaignPreferredGenderToSearchOptions(
  campaignPreferredGender: PreferredCreatorGender[]
): SingleSelectionFilterOptionsReturn | null {
  const gender = campaignPreferredGender[0];
  if (!gender?.name) return null;
  const genderObject = {
    id: gender?.name?.toLowerCase(),
    localeLabelKey: '',
    value: gender?.name?.toLowerCase(),
  };

  return {
    option: genderObject,
    filterSelectionType: 'singleSelection',
    filterGroupId: 'creatorDetails',
    filterId: 'gender',
  };
}

type PreferredLocationsInput = {
  cities?: PreferredCreatorCity[];
  states?: PreferredCreatorState[];
  countries?: PreferredCreatorCountry[];
};

type PreferredLocation =
  | PreferredCreatorCity
  | PreferredCreatorState
  | PreferredCreatorCountry;

const LOCATION_KIND = {
  CITY: 'city',
  STATE: 'state',
  COUNTRY: 'country',
} as const;

type LocationKind = (typeof LOCATION_KIND)[keyof typeof LOCATION_KIND];

const matchString = (expected?: string, actual?: string) =>
  !expected || expected.toLowerCase() === actual?.toLowerCase();

function findMatchingLocation(
  fetchedLocations: Awaited<ReturnType<typeof locationFetchers.locations>>,
  location: PreferredLocation,
  kind: LocationKind
) {
  const locationName = location.name;
  const locationCountry = 'country' in location ? location.country : undefined;
  const locationState = 'state' in location ? location.state : undefined;

  const matchers: Record<LocationKind, (fetched: FetchedLocation) => boolean> =
    {
      [LOCATION_KIND.CITY]: (fetched) =>
        matchString(locationName, fetched.value.place) &&
        matchString(locationState, fetched.value.region) &&
        matchString(locationCountry, fetched.value.country),
      [LOCATION_KIND.STATE]: (fetched) =>
        matchString(locationName, fetched.value.region) &&
        matchString(locationCountry, fetched.value.country),
      [LOCATION_KIND.COUNTRY]: (fetched) =>
        matchString(locationName, fetched.value.country),
    };

  return fetchedLocations.find((fetched) => matchers[kind](fetched));
}

async function fetchAndMatchLocation(
  location: PreferredLocation,
  kind: LocationKind
): Promise<SingleSelectionFilterOptionsReturn | null> {
  const locationName = location.name as string;
  const fetchedLocations = await locationFetchers.locations(locationName);
  const matchedLocation =
    findMatchingLocation(fetchedLocations, location, kind) ||
    fetchedLocations[0];

  if (!matchedLocation) return null;

  return {
    option: {
      id: matchedLocation.id,
      value: matchedLocation.value,
      localeLabelKey: matchedLocation.localeLabelKey,
    },
    filterSelectionType: 'multiSelectionAsync' as FilterSelection,
    filterGroupId: 'creatorDetails' as FiltersGroupId,
    filterId: 'location' as FilterIds,
  };
}

export async function campaignPreferredLocationsToSearchOptions(
  locations: PreferredLocationsInput
): Promise<SingleSelectionFilterOptionsReturn[]> {
  const {cities = [], states = [], countries = []} = locations;

  if (cities.length === 0 && states.length === 0 && countries.length === 0) {
    return [];
  }

  const cityPromises = cities.map((city) =>
    fetchAndMatchLocation(city, LOCATION_KIND.CITY)
  );

  const statePromises = states.map((state) =>
    fetchAndMatchLocation(state, LOCATION_KIND.STATE)
  );

  const countryPromises = countries.map((country) =>
    fetchAndMatchLocation(country, LOCATION_KIND.COUNTRY)
  );

  const results = await Promise.all([
    ...cityPromises,
    ...statePromises,
    ...countryPromises,
  ]);

  return results.filter(
    (result): result is SingleSelectionFilterOptionsReturn => result !== null
  );
}

export function combineResults(
  ...results: (CampaignFiltersStackResult | CampaignFiltersStackResult[])[]
): CampaignFiltersStackResult[] {
  const combinedArray: (
    | RangeFilterOptionsReturn
    | SingleSelectionFilterOptionsReturn
  )[] = [];

  results.forEach(
    (result: CampaignFiltersStackResult | CampaignFiltersStackResult[]) => {
      if (result) {
        if (Array.isArray(result)) {
          result.forEach((item) => {
            if (item) combinedArray.push(item);
          });
        } else {
          combinedArray.push(result);
        }
      }
    }
  );

  return combinedArray;
}
